import React, { useState, useEffect, memo } from "react";
import ReactSelect, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import makeAnimated from "react-select/animated";
import { FaIcon } from "./Icons.jsx";
import { Transition } from "react-transition-group";

/*
  Props:
  - options: [value: <val>, label: <label>]
  See react-select docs for more
*/

const SMEPrimary = "#47A9A6";

export const SMETheme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#a1dcda",
    primary50: "#83bfbc",
    primary75: "#6bafad",
    primary: SMEPrimary,
    danger: "#d9534f",
    dangerLight: "#e79997",
  },
});

export const customStyles = {
  container: (provided, state) => ({
    ...provided,
    boxShadow: "inset 0px 0px 4px rgb(0 0 0 / 15%)",
  }),
  control: (provided, state) => ({
    ...provided,
    minHeight: 34,
  }),
  menu: (provided, state) => ({
    ...provided,
    zIndex: 99999,
  }),
  menuPortal: (provided, state) => ({
    ...provided,
    zIndex: 99999,
  }),
  option: (provided, { isDisabled, isFocused }) => ({
    ...provided,
    color: isDisabled && isFocused ? "white" : provided.color,
    cursor: isDisabled ? 'not-allowed' : 'default',
  }),
  multiValue: (provided, state) => ({
    ...provided,
    backgroundColor: "#47a9a666",
    borderRadius: "3px",
    ":hover": {
      backgroundColor: "#47a9a6b3",
    },
  }),
  multiValueRemove: (provided, state) => ({
    ...provided,
    ":hover": {
      backgroundColor: "#47a9a6b3",
    },
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    margin: 0,
    display: 'none'
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: "#888888",
  }),
};

const animatedComponents = makeAnimated();

export const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <FaIcon type={`chevron-${props.selectProps?.menuIsOpen ? "up" : "down"}`} />
    </components.DropdownIndicator>
  );
};

// MultiValue component using custom Collapse
const MultiValue = ({ in: inProp, onExited, ...props }) => (
  <Collapse in={inProp} onExited={onExited}>
    <components.MultiValue cropWithEllipsis={inProp} {...props} />
  </Collapse>
);

// Flatten possible grouped options to one flat array by returning group options, or option itself if options are not grouped
const defaultGetSelectedOptions = (options, value, multiple) => { return options.flatMap(g => g.options || g).filter(o => multiple ? value.includes(o.value) : value == o.value ) }

// TODO: Refactor. This component is doing way too much
const Select = memo((
  { 
    defaultValue, 
    value: propsValue, 
    onChange = () => { },
    className,
    isMulti = false, 
    disabled, 
    styles = {},
    creatable = false,
    creatableOptions = [],
    addCreatableOption = () => {},
    readOnly,
    closeMenuOnSelect = true,
    selectAll = false,
    selectAllLabel = I18n.t("select_all_label"),
    categorizeSearch,
    selectionsLimit = 0,
    getSelectedOptions = defaultGetSelectedOptions,
    valueAccessor = (selectedOptions, isMulti) => isMulti ? selectedOptions : selectedOptions[0] || "",
    confirmClear = false,
    selectComponents = {},
    returnOptionObject = false,
    placeholder = `${I18n.t('select')}...`,
    ...rest
  }
) => {
  const [value, setValue] = useState(propsValue != undefined ? propsValue : defaultValue != undefined ? defaultValue : isMulti ? [] : "");
  const selectAllOption = {
    id: "<SELECT_ALL>",
    value: "<SELECT_ALL>",
    label: selectAllLabel
  };
  let options = selectAll ? [selectAllOption, ...rest.options, ...creatableOptions] : [...rest.options, ...creatableOptions]

  useEffect(() => {
    if (propsValue != undefined && propsValue !== value)
      setValue(propsValue != undefined ? propsValue : isMulti ? [] : "");
  }, [propsValue]);

  const handleChange = (values, actionMeta) => {
    const {action, option} = actionMeta;

    if (confirmClear && action == 'clear') {
      if (!confirm(I18n.t('clear_selection_confirm'))) return;
    }

    let shouldReturn = false;
    // The creatable options need to be handled on top level
    if (action == 'create-option') {
      (isMulti ? values : [values]).forEach(v => {
        if (v['__isNew__']) { 
          addCreatableOption(v.value);
          // Somehow, when new option is created, the options updated but even though that value is set here, not in the parent component.
          // So this will not select the newly created option.
          shouldReturn = true;
        }
      });
    }

    if (shouldReturn) return;

    const newValue = isMulti ? values.map(v => v.value) : returnOptionObject ? values : values?.value || '';

    if (propsValue === undefined) {
      setValue(newValue);
    }

    if (isMulti && selectAll && action === "select-option" && option.value === selectAllOption.value){
      onChange([...rest.options, ...creatableOptions].map(opt => opt.value));
    } else {
      onChange(newValue);
    }
  }

  const selectedOptions =  getSelectedOptions(options, value, isMulti);
  
  const SelectClass = creatable ? CreatableSelect : ReactSelect

  if (categorizeSearch) {
    options = options.map(item => ({
      ...item,
      options: item.options.map(option => ({
        ...option,
        category: item.text_label || item.label
      }))
    }));
  }
  
  const categoryFilterOption = (candidate, input) => {
    return (candidate.data.text_label || candidate.label).toLowerCase().includes(input.toLowerCase()) || candidate.data.category.toLowerCase().includes(input.toLowerCase());
  };

  return <>
    <SelectClass
      className={`react-select-bootstrap ${className || ""} ${disabled ? "disabled" : ""} ${readOnly ? "readonly" : ""}`}
      value={valueAccessor(selectedOptions, isMulti)}
      onChange={handleChange}
      isMulti={isMulti}
      theme={SMETheme}
      placeholder={placeholder}
      styles={{ ...customStyles, ...styles }}
      isDisabled={disabled || readOnly}
      isOptionDisabled={(o) => (o.disabled || (selectedOptions && selectionsLimit !== 0 && isMulti && selectedOptions.length >= selectionsLimit))}
      {...rest}
      options={options}
      components={{ ...animatedComponents, DropdownIndicator, MultiValue, ...selectComponents }}
      closeMenuOnSelect={closeMenuOnSelect}
      {...(categorizeSearch && {filterOption : categoryFilterOption})}
    />
  </>
});

export default Select;


export const ReadOnlyMultiSelectValues = ({values, wordWrap = true}) =>
  <div style={{display: 'flex', flexWrap: 'wrap'}}>{values.map((v, i) => <span key={v + i} className="react-select-value-readonly">{v.label}</span>)}</div>;

const MOUNT_TIMEOUT = 300; // This can be anything as long as it happens after the item has been rendered and has width > 0.
const TIMEOUT_DURATION = 260;
const NO_TIMEOUT_DURATION = 0;
const TRANSITION = `width ${TIMEOUT_DURATION}ms ease-out`;

// Credits to: https://github.com/JedWatson/react-select/issues/3029#issuecomment-675194189
// This has been modified to set the width after the item has been entered (so it has been mounted)
// Setting the width onExit, doesn't seem to add it quickly enough.
// Also we have added an appear and enter transitions to make sure all items trigger the onEntered callback.
const Collapse = ({ children, in: inProp, onExited }) => {
  const ref = React.useRef();
  const mountTimeout = React.useRef();

  const onEntered = React.useCallback(() => {
    if (ref.current) {
      mountTimeout.current = setTimeout(() => {
        if (!ref.current) return;
        const { width } = ref.current.getBoundingClientRect();
        ref.current.style.width = `${width}px`;
      }, MOUNT_TIMEOUT);
    }
  }, []);

  // Just in case the item gets unmounted before the timeout is executed.
  const onExit = React.useCallback(() => {
    if (mountTimeout.current) {
      clearTimeout(mountTimeout.current);
    }
  }, [mountTimeout.current]);

  return (
    <Transition
      appear={true}
      mountOnEnter
      unmountOnExit
      in={inProp}
      timeout={{ exit: TIMEOUT_DURATION, appear: NO_TIMEOUT_DURATION, enter: NO_TIMEOUT_DURATION }}
      onEntered={onEntered}
      onExit={onExit}
      onExited={onExited}
    >
      {(state) => (
        <div
          ref={ref}
          style={
            state === "exiting" || state === "exited"
              ? {
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  width: 0,
                  transition: TRANSITION,
                }
              : undefined
          }
        >
          {children}
        </div>
      )}
    </Transition>
  );
};

// Array of objects from backend will be formatted to [{label, value}] for options in Select
export const oldArrToSelectFormatter = (oldArr, value, label) => {
  return oldArr.map((item) => ({label: item[label], value: item[value]}))
}